/**
 * GeofencesPage — Geofence Editor route (DISPATCH-04 / GEO-02).
 *
 * Phase 9 / Plan 09-06 — replaces the Plan 09-04 stub with the real geofence
 * editor layout: a 320px sidebar sourced from GET /v1/sites (ALL company_sites,
 * including jobless ones — Blocker 2 fix) plus the Mapbox satellite editor.
 *
 * Layout: full-page, no resizable panes (UI-SPEC §6).
 *   left: GeofenceSidebar (320px, fixed) — searchable all-sites list
 *   right: GeofenceEditor (fills remaining width) — satellite map + draw
 *
 * The site list is sourced from useSites() (GET /v1/sites), NOT from useJobs.
 * A dispatcher can geofence a brand-new site before any job exists for it.
 *
 * Spec: UI-SPEC §6 (Geofence Editor surface); DISPATCH-04; D-01/D-02/D-05.
 */

import * as React from 'react';
import { useState } from 'react';
import { GeofenceSidebar } from '../features/geofences/GeofenceSidebar.js';
import { GeofenceEditor } from '../features/geofences/GeofenceEditor.js';
import { TenantGeofenceModePanel } from '../features/geofences/TenantGeofenceModePanel.js';
import { useSites } from '../features/geofences/use-sites.js';

export function GeofencesPage(): React.JSX.Element {
  const { data, isLoading, isError } = useSites();
  const sites = data?.sites ?? [];

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  // Resolve the selected site for the editor (to pass name + center coords)
  const selectedSite = sites.find((s) => s.siteId === selectedSiteId) ?? null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-primary)',
      }}
    >
      {/* Phase 8 / Task 8.2 — tenant geofence-mode config (DISPATCH_MANAGER+ to change). */}
      <TenantGeofenceModePanel />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left sidebar — 320px fixed */}
        {isLoading ? (
          <div
            style={{
              width: '320px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRight: '1px solid var(--color-border-primary)',
              color: 'var(--color-text-secondary)',
              fontSize: '14px',
              fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
            }}
          >
            Loading sites…
          </div>
        ) : isError ? (
          <div
            style={{
              width: '320px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRight: '1px solid var(--color-border-primary)',
              color: 'var(--color-danger)',
              fontSize: '14px',
              fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            Could not load sites. Retry Loading
          </div>
        ) : (
          <GeofenceSidebar
            sites={sites}
            selectedSiteId={selectedSiteId}
            onSelect={setSelectedSiteId}
          />
        )}

        {/* Right panel — Mapbox satellite editor */}
        <GeofenceEditor
          siteId={selectedSiteId}
          siteName={selectedSite?.siteName}
          centerLat={selectedSite?.centerLat}
          centerLng={selectedSite?.centerLng}
          siteType={selectedSite?.siteType ?? 'PICKUP'}
        />
      </div>
    </div>
  );
}
